import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// Rientro da Stripe Checkout: conferma il pagamento via polling dello stato
// (mai fidarsi del solo redirect). Il webhook/fallback lato server attiva il piano.
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading"); // loading | paid | failed | timeout

  useEffect(() => {
    if (!sessionId) { setState("failed"); return undefined; }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); clearInterval(timer); }
        else if (["failed", "expired"].includes(data.status)) { setState("failed"); clearInterval(timer); }
      } catch { /* retry al prossimo tick */ }
      if (attempts >= 25) { setState("timeout"); clearInterval(timer); }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="payment-success-page">
      <Card className="w-full max-w-md border-slate-200">
        <CardContent className="p-8 text-center space-y-4">
          {state === "loading" && (
            <>
              <Loader2 className="w-10 h-10 text-green-700 mx-auto animate-spin" aria-hidden="true" />
              <p className="text-sm font-semibold text-slate-900" data-testid="payment-status-text">
                Conferma del pagamento in corso…
              </p>
              <p className="text-xs text-slate-500">Attendi qualche secondo: stiamo attivando il piano.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-green-700 mx-auto" aria-hidden="true" />
              <p className="text-base font-bold text-slate-900" data-testid="payment-status-text">
                Pagamento completato
              </p>
              <p className="text-sm text-slate-600">
                Il piano è stato attivato sulla tua azienda. Trovi i dettagli in Impostazioni.
              </p>
              <Link to="/impostazioni">
                <Button className="bg-green-700 hover:bg-green-800" data-testid="payment-goto-settings">
                  Vai alle impostazioni
                </Button>
              </Link>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle className="w-10 h-10 text-amber-600 mx-auto" aria-hidden="true" />
              <p className="text-base font-bold text-slate-900" data-testid="payment-status-text">
                {state === "timeout" ? "Conferma in ritardo" : "Pagamento non completato"}
              </p>
              <p className="text-sm text-slate-600">
                {state === "timeout"
                  ? "Il pagamento risulta ancora in elaborazione: lo stato si aggiornerà automaticamente."
                  : "Nessun addebito effettuato. Puoi riprovare dalla pagina Impostazioni."}
              </p>
              <Link to="/impostazioni">
                <Button variant="outline" data-testid="payment-goto-settings">Torna alle impostazioni</Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
